import { OrderPreferences, User } from '@choco-admin/types';
import { toE164 } from '@choco-admin/utils';
import { useCallback } from 'react';

import { useUsersPageUpdateUserMutation } from '../../../generated';
import { mapToUser } from '../utils/userMapper';

export type CaptureExceptionFunction = (error: Error) => void;

/**
 * Every field but `id` is optional, and an omitted field is left untouched by
 * the backend. Callers editing an existing user should send only what changed —
 * the backend re-validates whatever it receives (see `buildUserUpdateInput`).
 */
export type UpdateUserInput = {
  id: string;
  name?: string;
  email?: string;
  /**
   * Login credential email for email-method users.  When provided the hook
   * lower-cases and trims it, then sends it as `loginEmail` in the mutation
   * (and also as `email` to keep both fields in sync — mirrors legacy
   * getUpdateUserVariables behaviour).
   */
  loginEmail?: string;
  /**
   * Pass-through for challengeByEmail when defined (do not send when absent so
   * the backend leaves the field untouched).
   */
  challengeByEmail?: boolean;
  phone?: string;
  locale?: string;
  isTest?: boolean;
  supplier?: boolean;
  supportLogin?: boolean;
  /**
   * Partial order-preference update from the form.  When present the hook
   * merges it OVER existingOrderPreferences before sending to the backend.
   * Omit this field entirely to leave the backend row untouched.
   */
  orderPreferences?: Partial<OrderPreferences>;
};

export type UseUpdateUserResponse = {
  updateUser: (
    input: UpdateUserInput,
    existingOrderPreferences?: OrderPreferences
  ) => Promise<User>;
  isLoading: boolean;
};

export type UseUpdateUserConfig = {
  captureException?: CaptureExceptionFunction;
};

export const useUpdateUser = (
  config: UseUpdateUserConfig
): UseUpdateUserResponse => {
  const { captureException } = config;

  const [updateUserMutation, { loading }] = useUsersPageUpdateUserMutation();

  const updateUser = useCallback(
    async (
      input: UpdateUserInput,
      existingOrderPreferences?: OrderPreferences
    ): Promise<User> => {
      try {
        // ── Backend PutItem contract ──────────────────────────────────────────
        // userService.userUpdate replaces the orderPreference DynamoDB row with
        // a full PutItem — it does NOT merge.  Any field omitted from the input
        // is therefore ERASED.  We enforce the contract here by merging the
        // caller's partial update OVER the user's existing preferences so every
        // mutation always sends the complete object.
        //
        // If orderPreferences is absent from the input, we omit the field from
        // the mutation entirely so the backend never touches the row.
        const mergedOrderPreference =
          input.orderPreferences !== undefined
            ? {
                // Start from the existing stored values as the baseline …
                pdf: existingOrderPreferences?.pdf ?? false,
                xlsx: existingOrderPreferences?.xlsx ?? false,
                sms: existingOrderPreferences?.sms ?? null,
                whatsApp: existingOrderPreferences?.whatsApp ?? null,
                fax: existingOrderPreferences?.fax ?? null,
                welcomeNotificationSent:
                  existingOrderPreferences?.welcomeNotificationSent ?? null,
                // … then layer the caller's changes on top.
                ...input.orderPreferences,
              }
            : undefined;

        // When a loginEmail is present (email-method users), mirror legacy:
        // lower-case + trim it, send as loginEmail, and also update email.
        const formattedLoginEmail = input.loginEmail
          ? input.loginEmail.toLowerCase().trim()
          : undefined;

        const result = await updateUserMutation({
          variables: {
            // Absent fields are left out of the payload entirely so the backend
            // neither re-validates nor rewrites them.
            userUpdateInput: {
              id: input.id,
              ...(input.name !== undefined && { name: input.name }),
              // For email-login users loginEmail drives the credential; we
              // also write email so the contact field stays consistent.
              ...(formattedLoginEmail !== undefined && {
                email: formattedLoginEmail,
                loginEmail: formattedLoginEmail,
              }),
              ...(formattedLoginEmail === undefined &&
                input.email !== undefined && { email: input.email }),
              ...(input.challengeByEmail !== undefined && {
                challengeByEmail: input.challengeByEmail,
              }),
              // Last line of defence before the phone reaches storage: a
              // non-E.164 value breaks Cognito signup and phone search, and
              // `userUpdate` is the one write path with no server-side
              // normalisation (CRM-576). An unparseable value is sent through
              // unchanged so the backend still reports the validation error.
              ...(input.phone !== undefined && {
                phone: toE164(input.phone) || input.phone,
              }),
              ...(input.locale !== undefined && { locale: input.locale }),
              ...(input.isTest !== undefined && { isTest: input.isTest }),
              ...(input.supplier !== undefined && {
                supplier: input.supplier,
              }),
              ...(input.supportLogin !== undefined && {
                chocoUser: input.supportLogin,
              }),
              ...(mergedOrderPreference !== undefined && {
                orderPreference: mergedOrderPreference,
              }),
            },
          },
          update: (cache, { data: mutationData }) => {
            if (mutationData?.userUpdate) {
              cache.modify({
                fields: {
                  userSearch: (existing) => {
                    return existing;
                  },
                },
              });
            }
          },
        });

        if (!result.data?.userUpdate) {
          throw new Error('Failed to update user');
        }

        const updatedUser = result.data.userUpdate;

        // Map to generic User type using shared mapper
        return mapToUser(updatedUser);
      } catch (error) {
        captureException?.(error as Error);
        throw error;
      }
    },
    [updateUserMutation, captureException]
  );

  return {
    updateUser,
    isLoading: loading,
  };
};
